import { createClient } from "@supabase/supabase-js";
import { proposalStoragePath, jobProjectMismatch } from "./jobMath";

const SUPABASE_URL      = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
  global: { fetch: fetch.bind(globalThis) },
});

// ─── Data shape converters ────────────────────────────────────────────────────
export const rowToTemplate = (row) => {
  const tpl = {
    id:            row.id,
    name:          row.name,
    description:   row.description || "",
    folderId:      row.folder_id,
    isExcelImport: row.is_excel_import,
    isProctor:     row.is_proctor,
    file_path:     row.file_path,
    sections:      row.data?.sections || [],
    ...(row.data || {}),
  };
  // Runtime detection: catch old imports where is_proctor wasn't set in the DB
  if (!tpl.isProctor && tpl.isExcelImport) {
    const names = [tpl.name, ...(tpl.sheets || []).map(s => s.name)];
    if (names.some(n => n && /^proctor/i.test(n))) tpl.isProctor = true;
  }
  return tpl;
};

export const rowToReport = (row) => ({
  ...(row.data || {}),
  id:            row.id,
  folderId:      row.folder_id,
  _templateId:   row.template_id,
  reportTitle:   row.name,
});

export const rowToFolder = (row) => ({ id: row.id, name: row.name, parentId: row.parent_id || null });

function rowToRateCatalogItem(row) {
  return {
    id: row.id,
    projectId: row.project_id,
    category: row.category,
    name: row.name,
    data: row.data || {},
    createdBy: row.created_by,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

// A job's header fields (property, address, contact, status, markupPercent,
// lines, frozen, revision, revisionOf) all live in row.data, exactly like
// rowToReport spreads row.data for a report — see that function above.
// Final fix wave, item 2: projectId comes from the row's own project_id
// (never from data), so upsertJob can refuse to write a job into any other
// project.
function rowToJob(row) {
  return {
    ...(row.data || {}),
    id: row.id,
    projectId: row.project_id,
    folderId: row.folder_id,
    name: row.name,
    createdBy: row.created_by,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

// ─── Auth ─────────────────────────────────────────────────────────────────────
export const signIn = (email, password) =>
  supabase.auth.signInWithPassword({ email, password });

export const signOut = () => supabase.auth.signOut();

export const getProfile = async (userId) => {
  const { data } = await supabase
    .from("profiles")
    .select("*")
    .eq("id", userId)
    .single();
  return data;
};

// ─── Projects ─────────────────────────────────────────────────────────────────
export const getUserProjects = async () => {
  const { data } = await supabase
    .from("projects")
    .select("*, project_members!inner(role)")
    .order("created_at");
  return (data || []).map(p => ({ ...p, myRole: p.project_members?.[0]?.role }));
};

export const getAllProjects = async () => {
  const { data } = await supabase.from("projects").select("*").order("created_at");
  return data || [];
};

export const createProject = async (name) => {
  const { data, error } = await supabase
    .from("projects").insert({ name }).select().single();
  return { data, error };
};

export const deleteProject = async (id) =>
  supabase.from("projects").delete().eq("id", id);

export const updateProjectConfig = async (projectId, config) => {
  const { error } = await supabase.from("projects").update({ config }).eq("id", projectId);
  return { error };
};

// ─── Members ──────────────────────────────────────────────────────────────────
export const getProjectMembers = async (projectId) => {
  const { data } = await supabase
    .from("project_members")
    .select("*, profiles(id, email, full_name)")
    .eq("project_id", projectId);
  return data || [];
};

export const addMemberToProject = async (projectId, userId, role) =>
  supabase.from("project_members").insert({ project_id: projectId, user_id: userId, role });

export const removeMember = async (projectId, userId) =>
  supabase.from("project_members").delete()
    .eq("project_id", projectId).eq("user_id", userId);

export const changeMemberRole = async (projectId, userId, role) =>
  supabase.from("project_members").update({ role })
    .eq("project_id", projectId).eq("user_id", userId);

// Create user via Edge Function (service role key lives there, not in client)
export const createUser = async (email, password, fullName, role, projectId) => {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) return { error: "Not authenticated. Please log in again." };
  const res = await fetch(`${SUPABASE_URL}/functions/v1/create-user`, {
    method: "POST",
    headers: {
      "Content-Type":  "application/json",
      "Authorization": `Bearer ${session.access_token}`,
      "apikey":        SUPABASE_ANON_KEY,
    },
    body: JSON.stringify({ email, password, fullName, role, projectId }),
  });
  const json = await res.json();
  if (!res.ok) return { error: json.message || json.error || `Error ${res.status}` };
  return json;
};

export const getAllUsers = async () => {
  const { data } = await supabase
    .from("profiles").select("*").order("created_at");
  return data || [];
};

export const setSuperAdmin = async (userId, isSuperAdmin) =>
  supabase.from("profiles").update({ is_super_admin: isSuperAdmin }).eq("id", userId);

// ─── Folders ──────────────────────────────────────────────────────────────────
const KIND_TO_TYPE = { tpl: "template", rep: "report", sheet: "sheet", spec: "spec" };

export const getFolders = async (projectId, kind) => {
  const { data } = await supabase
    .from("folders").select("*")
    .eq("project_id", projectId)
    .eq("type", KIND_TO_TYPE[kind] || kind)
    .order("created_at");
  return (data || []).map(rowToFolder);
};

export const createFolder = async (projectId, kind, name, parentId = null) => {
  const { data, error } = await supabase
    .from("folders")
    .insert({ project_id: projectId, type: KIND_TO_TYPE[kind] || kind, name, parent_id: parentId || null })
    .select().single();
  return { data: data ? rowToFolder(data) : null, error };
};

export const renameFolder = async (id, name) =>
  supabase.from("folders").update({ name }).eq("id", id);

export const deleteFolder = async (id) =>
  supabase.from("folders").delete().eq("id", id);

// ─── Templates ────────────────────────────────────────────────────────────────
export const getTemplates = async (projectId) => {
  const { data } = await supabase
    .from("templates").select("*").eq("project_id", projectId).order("created_at");
  return (data || []).map(rowToTemplate);
};

export const upsertTemplate = async (tpl, projectId) => {
  const { data, error } = await supabase.from("templates").upsert({
    id:            tpl.id,
    project_id:    projectId,
    folder_id:     tpl.folderId || null,
    name:          tpl.name,
    description:   tpl.description || "",
    data:          {
      sections: tpl.sections || [],
      ...(tpl.isMultiSheet ? { isMultiSheet: true, sheets: tpl.sheets } : {}),
      ...(tpl.isPdf ? { isPdf: true } : {}),
    },
    is_excel_import: !!tpl.isExcelImport,
    is_proctor:    !!tpl.isProctor,
    file_path:     tpl.file_path || null,
    updated_at:    new Date().toISOString(),
  }).select().single();
  if (error) console.error("[upsertTemplate] DB error:", error);
  return { data: data ? rowToTemplate(data) : null, error };
};

export const deleteTemplate = async (id) =>
  supabase.from("templates").delete().eq("id", id);

export const uploadTemplateFile = async (projectId, templateId, bytes, mimeType) => {
  const path = `${projectId}/${templateId}`;
  const type = mimeType || "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
  const blob = new Blob([bytes], { type });
  const { error } = await supabase.storage
    .from("template-files").upload(path, blob, { upsert: true });
  return { path, error };
};

export const downloadTemplateFile = async (path) => {
  const { data, error } = await supabase.storage.from("template-files").download(path);
  if (error || !data) return null;
  return new Uint8Array(await data.arrayBuffer());
};

// ─── Reports ──────────────────────────────────────────────────────────────────
export const getReports = async (projectId) => {
  const { data } = await supabase
    .from("reports").select("*").eq("project_id", projectId)
    .order("created_at", { ascending: false });
  return (data || []).map(rowToReport);
};

export const upsertReport = async (rep, projectId) => {
  const { data, error } = await supabase.from("reports").upsert({
    id:          rep.id,
    project_id:  projectId,
    folder_id:   rep.folderId || null,
    template_id: rep._templateId || null,
    name:        rep.reportTitle || "Untitled Report",
    data:        rep,
    updated_at:  new Date().toISOString(),
  }).select().single();
  if (error) console.error("[upsertReport] DB error:", error);
  return { data: data ? rowToReport(data) : null, error };
};

export const deleteReport = async (id) =>
  supabase.from("reports").delete().eq("id", id);

// ─── Sheets ───────────────────────────────────────────────────────────────────
export const getSheets = async (projectId) => {
  const { data } = await supabase
    .from("sheets").select("*").eq("project_id", projectId).order("created_at");
  return (data || []).map(r => ({
    id: r.id, name: r.name, folderId: r.folder_id,
    file_path: r.file_path, size: r.file_size, uploadedAt: r.created_at,
  }));
};

export const uploadSheet = async (projectId, id, name, folderId, bytes) => {
  const path = `${projectId}/${id}.pdf`;
  const blob = new Blob([bytes], { type: "application/pdf" });
  await supabase.storage.from("sheet-files").upload(path, blob, { upsert: true });
  const { data, error } = await supabase.from("sheets").insert({
    id, project_id: projectId, folder_id: folderId || null,
    name, file_path: path, file_size: bytes.byteLength,
  }).select().single();
  return { data, error };
};

export const downloadSheet = async (path) => {
  const { data } = await supabase.storage.from("sheet-files").download(path);
  if (!data) return null;
  return data.arrayBuffer();
};

export const deleteSheetRecord = async (id, path) => {
  if (path) await supabase.storage.from("sheet-files").remove([path]);
  return supabase.from("sheets").delete().eq("id", id);
};

export const updateSheetMeta = async (id, updates) =>
  supabase.from("sheets").update(updates).eq("id", id);

// ─── Specs ────────────────────────────────────────────────────────────────────
export const getSpecs = async (projectId) => {
  const { data } = await supabase
    .from("specs").select("*").eq("project_id", projectId).order("created_at");
  return (data || []).map(r => ({
    id: r.id, name: r.name, folderId: r.folder_id,
    file_path: r.file_path, size: r.file_size, uploadedAt: r.created_at,
  }));
};

export const uploadSpec = async (projectId, id, name, folderId, bytes) => {
  const path = `${projectId}/${id}.pdf`;
  const blob = new Blob([bytes], { type: "application/pdf" });
  await supabase.storage.from("spec-files").upload(path, blob, { upsert: true });
  const { data, error } = await supabase.from("specs").insert({
    id, project_id: projectId, folder_id: folderId || null,
    name, file_path: path, file_size: bytes.byteLength,
  }).select().single();
  return { data, error };
};

export const downloadSpec = async (path) => {
  const { data } = await supabase.storage.from("spec-files").download(path);
  if (!data) return null;
  return data.arrayBuffer();
};

export const deleteSpecRecord = async (id, path) => {
  if (path) await supabase.storage.from("spec-files").remove([path]);
  return supabase.from("specs").delete().eq("id", id);
};

export const updateSpecMeta = async (id, updates) =>
  supabase.from("specs").update(updates).eq("id", id);

// ─── Rate Catalog ─────────────────────────────────────────────────────────────
// Throws on a query error instead of returning []. "Update unit types" needs
// this: an empty list would otherwise surface as a misleading "Rate catalog
// is missing an entry" message when the real problem is the fetch.
export async function getRateCatalogItemsOrThrow(projectId) {
  const { data, error } = await supabase
    .from("rate_catalog_items")
    .select("*")
    .eq("project_id", projectId)
    .order("category")
    .order("name");
  if (error) throw new Error(error.message || String(error));
  return (data || []).map(rowToRateCatalogItem);
}

// Existing behaviour for its existing callers (RateCatalog.jsx): logs and
// returns [] on error.
export async function getRateCatalogItems(projectId) {
  try {
    return await getRateCatalogItemsOrThrow(projectId);
  } catch (error) {
    console.error("getRateCatalogItems", error);
    return [];
  }
}

export async function upsertRateCatalogItem(item, projectId) {
  const row = {
    id: item.id,
    project_id: projectId,
    category: item.category,
    name: item.name,
    data: item.data || {},
    updated_at: new Date().toISOString(),
  };
  const { data, error } = await supabase
    .from("rate_catalog_items")
    .upsert(row)
    .select()
    .single();
  if (error) console.error("upsertRateCatalogItem", error);
  return { data: data ? rowToRateCatalogItem(data) : null, error };
}

export async function deleteRateCatalogItem(id) {
  const { error } = await supabase.from("rate_catalog_items").delete().eq("id", id);
  if (error) console.error("deleteRateCatalogItem", error);
  return { error };
}

// ─── Jobs ─────────────────────────────────────────────────────────────────────
export const getJobs = async (projectId) => {
  const { data } = await supabase
    .from("jobs").select("*").eq("project_id", projectId)
    .order("created_at", { ascending: false });
  return (data || []).map(rowToJob);
};

// Task 6 fix round 1 (ruling R18): the stored copy of one job, read just
// before a job write so a bid marked Sent in another tab or by another user
// is never overwritten. Unlike getJobs, the error is returned, never
// swallowed: the caller refuses the write when the check itself fails.
// data is null (with no error) when the row does not exist.
export const getJob = async (id) => {
  const { data, error } = await supabase.from("jobs").select("*").eq("id", id).maybeSingle();
  if (error) console.error("[getJob] DB error:", error);
  return { data: data ? rowToJob(data) : null, error };
};

// Same as getJobs, but returns the error instead of an empty list, for the
// stored-row check before a quantity write-through to a takeoff. Final fix
// wave, item 11: also the project load, which must tell "no jobs" from
// "could not load jobs"; ordered like getJobs for the list (the other
// callers look rows up by id, so the order changes nothing for them).
export const getJobsChecked = async (projectId) => {
  const { data, error } = await supabase.from("jobs").select("*").eq("project_id", projectId)
    .order("created_at", { ascending: false });
  if (error) console.error("[getJobsChecked] DB error:", error);
  return { data: data ? data.map(rowToJob) : null, error };
};

// Final fix wave, item 2: refuses, without writing, a job that belongs to
// another project (jobMath.jobProjectMismatch). The schema trigger refuses
// a project_id change as well.
export const upsertJob = async (job, projectId) => {
  const mismatch = jobProjectMismatch(job, projectId);
  if (mismatch) return { data: null, error: new Error(mismatch) };
  const { data, error } = await supabase.from("jobs").upsert({
    id:         job.id,
    project_id: projectId,
    folder_id:  job.folderId || null,
    name:       job.name || "Untitled Job",
    data:       job,
    updated_at: new Date().toISOString(),
  }).select().single();
  if (error) console.error("[upsertJob] DB error:", error);
  return { data: data ? rowToJob(data) : null, error };
};

export const deleteJob = async (id) =>
  supabase.from("jobs").delete().eq("id", id);

// ─── Proposals ────────────────────────────────────────────────────────────────
// Proposal PDFs live in the existing template-files bucket. Its RLS
// (storage_path_project_id() in supabase-schema.sql) only checks that the
// FIRST path segment is a project_id the caller belongs to (select: any
// member; insert/delete: admin or manager), so a {project_id}/proposals/...
// path is already covered with no schema or policy change.
//
// upsert is false on purpose: a stored proposal is never overwritten.
// Task 7 fix round 1 (ruling R21): every call uploads to a path of its own
// ({project}/proposals/{job}-R{rev}-{attempt}.pdf, jobMath.proposalStoragePath),
// so a file left behind by an earlier failed attempt never blocks a retry.
// The caller records the returned path in frozen.proposalPath.
export const uploadProposalPdf = async (projectId, jobId, revision, blob, attemptId = crypto.randomUUID()) => {
  const path = proposalStoragePath(projectId, jobId, revision, attemptId);
  const { error } = await supabase.storage
    .from("template-files").upload(path, blob, { upsert: false, contentType: "application/pdf" });
  return { path, error };
};

export const downloadProposalPdf = async (path) => {
  const { data, error } = await supabase.storage.from("template-files").download(path);
  if (error || !data) return null;
  return data; // a Blob
};

// Removes a proposal PDF that was uploaded for a Mark Sent whose job write
// then failed. Storage reports no error when RLS (or a missing file) means
// nothing was deleted, so an empty result counts as a failure here: the
// caller must never claim a file was removed when it may still exist.
export const removeProposalPdf = async (path) => {
  const { data, error } = await supabase.storage.from("template-files").remove([path]);
  if (error) return { error };
  if (!data || data.length === 0) return { error: new Error("Storage reported that nothing was removed.") };
  return { error: null };
};
